import React, {useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';

import {
  BriefcaseIcon,
  ChatAlt2Icon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  VolumeUpIcon,
} from '@heroicons/react/outline';
import DropdownInput from '../../components/DropdownInput';
import Button, {ButtonTypes} from '../../components/ButtonNew';
import {CoachingService} from '../../constants/coaching';
import {cx, createURLWithParams} from '../../util';

const renderCategoryIcon = (Icon) => (
  <Icon className="w-4 h-4 mr-2" aria-hidden="true" />
);

const CoachingFilters = ({
  role,
  company,
  category,
  skill,
  industry,
  onChange,
  tags,
  basePath, // option-link base; tag landing pages pass their /coaching handoff URL
}) => {
  const router = useRouter();
  const urlBase = basePath || router.asPath;
  const sentinelRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      {threshold: 0},
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleCategoryClick = (e, value) => {
    // Applies new category or clears category if active
    e.preventDefault();
    onChange('category', category === value ? null : value);
  };

  const toRoleOption = ({name, id}) => ({
    label: name,
    value: id,
    url: createURLWithParams(urlBase, {role: id}),
  });
  const toCompanyOption = ({name, id}) => ({
    label: name,
    value: id,
    url: createURLWithParams(urlBase, {company: id}),
  });
  const toSkillOption = ({name, id}) => ({
    label: name,
    value: id,
    // Don't use anchor tag for this filter to avoid "web crawler traps"
  });
  const toIndustryOption = ({name, id}) => ({
    label: name,
    value: id,
    // Don't use anchor tag for this filter to avoid "web crawler traps"
  });

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" className="h-px -mb-px" />
      <div className={cx('sticky top-0 z-20 -mx-[var(--page-gutter,1.5rem)] bg-white', isStuck && "after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-screen after:h-px after:bg-gray-200")}>
        <div className="flex space-x-2 overflow-x-auto px-[var(--page-gutter,1.5rem)] py-3 scrollbar-hidden">
          <DropdownInput
            value={role}
            placeholder="Role"
            options={tags ? tags.role.map(toRoleOption).filter((r) => r.label !== 'Recruiter') : []}
            onChange={(val) => onChange('role', val)}
            showFilter
          />
          <DropdownInput
            value={company}
            placeholder="Company"
            options={tags ? tags.company.map(toCompanyOption) : []}
            onChange={(val) => onChange('company', val)}
            showFilter
          />
          <DropdownInput
            value={skill}
            placeholder="Skill"
            options={tags ? tags.skill.map(toSkillOption) : []}
            onChange={(val) => onChange('skill', val)}
            showFilter
          />
          <DropdownInput
            value={industry}
            placeholder="Industry"
            options={tags ? tags.industry.map(toIndustryOption) : []}
            onChange={(val) => onChange('industry', val)}
          />
          <Button
            buttonType={ButtonTypes.SECONDARY}
            onClick={(e) => handleCategoryClick(e, CoachingService.MOCK_INTERVIEW)}
            href={`/coaching?category=${CoachingService.MOCK_INTERVIEW}`}
            className={cx('pl-4 whitespace-nowrap', {
              'text-indigo-600 border-indigo-600': category === CoachingService.MOCK_INTERVIEW,
            })}
          >
            {renderCategoryIcon(ChatAlt2Icon)}
            Interviewing
          </Button>
          <Button
            buttonType={ButtonTypes.SECONDARY}
            onClick={(e) => handleCategoryClick(e, CoachingService.CAREER_COACHING)}
            href={`/coaching?category=${CoachingService.CAREER_COACHING}`}
            className={cx('pl-4 whitespace-nowrap', {
              'text-indigo-600 border-indigo-600': category === CoachingService.CAREER_COACHING,
            })}
          >
            {renderCategoryIcon(BriefcaseIcon)}
            Career
          </Button>
          <Button
            buttonType={ButtonTypes.SECONDARY}
            onClick={(e) => handleCategoryClick(e, CoachingService.NEGOTIATION)}
            href={`/coaching?category=${CoachingService.NEGOTIATION}`}
            className={cx('pl-4 whitespace-nowrap', {
              'text-indigo-600 border-indigo-600': category === CoachingService.NEGOTIATION,
            })}
          >
            {renderCategoryIcon(CurrencyDollarIcon)}
            Negotiation
          </Button>
          <Button
            buttonType={ButtonTypes.SECONDARY}
            onClick={(e) => handleCategoryClick(e, CoachingService.COMMUNICATION)}
            href={`/coaching?category=${CoachingService.COMMUNICATION}`}
            className={cx('pl-4 whitespace-nowrap', {
              'text-indigo-600 border-indigo-600': category === CoachingService.COMMUNICATION,
            })}
          >
            {renderCategoryIcon(VolumeUpIcon)}
            Communication
          </Button>
          <Button
            buttonType={ButtonTypes.SECONDARY}
            onClick={(e) => handleCategoryClick(e, CoachingService.RESUME_REVIEW)}
            href={`/coaching?category=${CoachingService.RESUME_REVIEW}`}
            className={cx('pl-4 whitespace-nowrap', {
              'text-indigo-600 border-indigo-600': category === CoachingService.RESUME_REVIEW,
            })}
          >
            {renderCategoryIcon(DocumentTextIcon)}
            Resume
          </Button>
        </div>
      </div>
    </>
  );
};

export default CoachingFilters;
